import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Dumbbell, Clock, Plus, ChevronRight, History, MessageSquare, CalendarPlus, AlertTriangle, Pencil, Save } from 'lucide-react';
import { workoutApi } from '@/services/api';
import { Card, Button, Calendar, Modal } from '@/components/common';
import { useUIStore } from '@/store/uiStore';
import type { Workout } from '@/types';

const Workouts = () => {
  const navigate = useNavigate();
  const { showToast } = useUIStore();
  const [workouts, setWorkouts] = useState<Workout[]>([]);
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [showActiveWorkoutWarning, setShowActiveWorkoutWarning] = useState(false);

  // Notes editing state
  const [editingNotesWorkoutId, setEditingNotesWorkoutId] = useState<string | null>(null);
  const [notesText, setNotesText] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  // Duration editing state
  const [editingDurationWorkoutId, setEditingDurationWorkoutId] = useState<string | null>(null);
  const [durationStartTime, setDurationStartTime] = useState('');
  const [durationEndTime, setDurationEndTime] = useState('');
  const [savingDuration, setSavingDuration] = useState(false);

  const loadWorkouts = async (loadMore = false) => {
    if (loadMore && !hasMore) return;

    try {
      const response = await workoutApi.getAll(loadMore ? cursor || undefined : undefined);
      if (response.success && response.data) {
        if (loadMore) {
          setWorkouts((prev) => [...prev, ...response.data!.items]);
        } else {
          setWorkouts(response.data.items);
        }
        setCursor(response.data.nextCursor);
        setHasMore(!!response.data.nextCursor);
      }
    } catch (error) {
      console.error('Failed to load workouts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWorkouts();
  }, []);

  const formatDuration = (start: string, end: string | null) => {
    if (!end) return 'Folyamatban';
    
    const startDate = new Date(start);
    const endDate = new Date(end);
    const durationMs = endDate.getTime() - startDate.getTime();
    const minutes = Math.floor(durationMs / 60000);
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours > 0) {
      return `${hours} óra ${remainingMinutes} perc`;
    }
    return `${minutes} perc`;
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('hu-HU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Convert ISO date to datetime-local input value (local timezone)
  const toDatetimeLocal = (isoString: string) => {
    const d = new Date(isoString);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Get dates with workouts for calendar highlighting
  const workoutDates = useMemo(() => {
    return workouts.map(w => new Date(w.startTime));
  }, [workouts]);

  // Check if there's an active workout (no endTime)
  const hasActiveWorkout = useMemo(() => {
    return workouts.some(w => !w.endTime);
  }, [workouts]);

  // Handle backdate button click - check for active workout first
  const handleBackdateClick = () => {
    if (hasActiveWorkout) {
      setShowActiveWorkoutWarning(true);
    } else {
      navigate('/workout?backdate=true');
    }
  };

  // Open notes editing modal
  const openNotesEditor = (workout: Workout) => {
    setEditingNotesWorkoutId(workout.id);
    setNotesText(workout.notes || '');
  };

  // Save notes
  const handleSaveNotes = async () => {
    if (!editingNotesWorkoutId) return;
    setSavingNotes(true);
    try {
      const response = await workoutApi.update(editingNotesWorkoutId, {
        notes: notesText.trim() || null,
      });
      if (response.success && response.data) {
        setWorkouts((prev) =>
          prev.map((w) => (w.id === editingNotesWorkoutId ? response.data!.workout : w))
        );
        showToast('Megjegyzés mentve', 'success');
        setEditingNotesWorkoutId(null);
      }
    } catch (error) {
      console.error('Failed to save notes:', error);
      showToast('Hiba a megjegyzés mentésekor', 'error');
    } finally {
      setSavingNotes(false);
    }
  };

  // Open duration editing modal
  const openDurationEditor = (workout: Workout) => {
    setEditingDurationWorkoutId(workout.id);
    setDurationStartTime(toDatetimeLocal(workout.startTime));
    setDurationEndTime(workout.endTime ? toDatetimeLocal(workout.endTime) : '');
  };

  // Save duration
  const handleSaveDuration = async () => {
    if (!editingDurationWorkoutId) return;

    if (!durationStartTime) {
      showToast('A kezdési idő megadása kötelező', 'warning');
      return;
    }

    const startDate = new Date(durationStartTime);
    const endDate = durationEndTime ? new Date(durationEndTime) : null;

    if (endDate && endDate <= startDate) {
      showToast('A befejezési időnek a kezdés után kell lennie', 'warning');
      return;
    }

    setSavingDuration(true);
    try {
      const updateData: { startTime: string; endTime?: string } = {
        startTime: startDate.toISOString(),
      };
      if (endDate) {
        updateData.endTime = endDate.toISOString();
      }

      const response = await workoutApi.update(editingDurationWorkoutId, updateData);
      if (response.success && response.data) {
        setWorkouts((prev) =>
          prev.map((w) => (w.id === editingDurationWorkoutId ? response.data!.workout : w))
        );
        showToast('Időtartam módosítva', 'success');
        setEditingDurationWorkoutId(null);
      }
    } catch (error) {
      console.error('Failed to save duration:', error);
      showToast('Hiba az időtartam mentésekor', 'error');
    } finally {
      setSavingDuration(false);
    }
  };

  // Filter workouts by selected date
  const filteredWorkouts = useMemo(() => {
    if (!selectedDate) return workouts;
    
    return workouts.filter(workout => {
      const workoutDate = new Date(workout.startTime);
      return (
        workoutDate.getFullYear() === selectedDate.getFullYear() &&
        workoutDate.getMonth() === selectedDate.getMonth() &&
        workoutDate.getDate() === selectedDate.getDate()
      );
    });
  }, [workouts, selectedDate]);

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Edzés előzmények</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1 sm:mt-2 text-sm sm:text-base">Korábbi edzéseid áttekintése</p>
        </div>
        <div className="flex flex-shrink-0 gap-2">
          <Button 
            size="sm" 
            variant="outline" 
            icon={<CalendarPlus className="w-4 h-4" />}
            onClick={handleBackdateClick}
          >
            Korábbi edzés
          </Button>
          <Link to="/workout">
            <Button size="sm" icon={<Plus className="w-4 h-4" />}>
              Új edzés
            </Button>
          </Link>
        </div>
      </div>

      {/* Calendar */}
      <div className="mb-6">
        <Calendar
          highlightedDates={workoutDates}
          selectedDate={selectedDate}
          onSelectDate={setSelectedDate}
        />
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filteredWorkouts.length === 0 && selectedDate ? (
        <Card>
          <div className="text-center py-8">
            <History className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Nincs edzés ezen a napon
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {selectedDate.toLocaleDateString('hu-HU', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
            <Button variant="outline" size="sm" onClick={() => setSelectedDate(null)}>
              Szűrés törlése
            </Button>
          </div>
        </Card>
      ) : workouts.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <History className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Nincs még edzésed</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Indíts el egy új edzést és rögzítsd a gyakorlataidat!
            </p>
            <Link to="/workout">
              <Button icon={<Plus className="w-5 h-5" />}>
                Első edzés indítása
              </Button>
            </Link>
          </div>
        </Card>
      ) : (
        <>
          <div className="space-y-4">
            {filteredWorkouts.map((workout) => (
              <Card key={workout.id}>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-start sm:items-center gap-3 min-w-0">
                    <div className={`p-2 sm:p-3 rounded-lg flex-shrink-0 ${workout.endTime ? 'bg-green-100' : 'bg-yellow-100'}`}>
                      <Dumbbell className={`w-5 h-5 sm:w-6 sm:h-6 ${workout.endTime ? 'text-green-600' : 'text-yellow-600'}`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-sm sm:text-base">
                          {formatDate(workout.startTime)}
                        </h3>
                        {!workout.endTime && (
                          <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded whitespace-nowrap">
                            Folyamatban
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                          {formatDuration(workout.startTime, workout.endTime)}
                          {workout.endTime && (
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                openDurationEditor(workout);
                              }}
                              className="ml-0.5 p-0.5 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors rounded"
                              title="Időtartam módosítása"
                            >
                              <Pencil className="w-3 h-3" />
                            </button>
                          )}
                        </span>
                        <span className="flex items-center gap-1">
                          <Dumbbell className="w-3 h-3 sm:w-4 sm:h-4" />
                          {workout.exercises.length} gyakorlat
                        </span>
                        {workout.bodyWeight && (
                          <span className="whitespace-nowrap">Testsúly: {workout.bodyWeight} kg</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <Link
                    to={`/workout?view=${workout.id}`}
                    className="px-3 py-1.5 text-sm text-primary-600 hover:bg-primary-50 rounded-lg flex items-center gap-1 self-end sm:self-auto flex-shrink-0"
                  >
                    Részletek
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>

                {workout.exercises.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex flex-wrap gap-2">
                      {workout.exercises.slice(0, 5).map((exercise) => {
                        const elapsedSeconds = (exercise as { elapsedSeconds?: number }).elapsedSeconds || 0;
                        const minutes = Math.floor(elapsedSeconds / 60);
                        const seconds = elapsedSeconds % 60;
                        const timeStr = `${minutes}:${seconds.toString().padStart(2, '0')}`;
                        const exerciseName = exercise.bodyweightExercise?.name || exercise.machine?.name || 'Ismeretlen';
                        
                        return (
                          <span
                            key={exercise.id}
                            className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm rounded"
                          >
                            {exerciseName}
                            <span className="text-gray-500 dark:text-gray-400 ml-1">
                              ({exercise.sets.length} sor{elapsedSeconds > 0 ? `, ${timeStr}` : ''})
                            </span>
                          </span>
                        );
                      })}
                      {workout.exercises.length > 5 && (
                        <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm rounded">
                          +{workout.exercises.length - 5} további
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Notes section - always visible with edit option */}
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  {workout.notes ? (
                    <button
                      onClick={() => openNotesEditor(workout)}
                      className="flex items-start gap-2 w-full text-left group"
                    >
                      <MessageSquare className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-gray-600 dark:text-gray-400 italic flex-1">
                        {workout.notes}
                      </p>
                      <Pencil className="w-3.5 h-3.5 text-gray-300 group-hover:text-primary-500 transition-colors flex-shrink-0 mt-0.5" />
                    </button>
                  ) : (
                    <button
                      onClick={() => openNotesEditor(workout)}
                      className="flex items-center gap-2 text-sm text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                    >
                      <MessageSquare className="w-4 h-4" />
                      <span>Megjegyzés hozzáadása...</span>
                    </button>
                  )}
                </div>
              </Card>
            ))}
          </div>

          {hasMore && (
            <div className="text-center mt-6">
              <Button variant="outline" onClick={() => loadWorkouts(true)}>
                Több betöltése
              </Button>
            </div>
          )}
        </>
      )}

      {/* Notes Editing Modal */}
      <Modal
        isOpen={!!editingNotesWorkoutId}
        onClose={() => setEditingNotesWorkoutId(null)}
        title="Megjegyzés az edzéshez"
        size="md"
        footer={
          <>
            <button
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              onClick={() => setEditingNotesWorkoutId(null)}
              disabled={savingNotes}
            >
              Mégse
            </button>
            <Button
              onClick={handleSaveNotes}
              disabled={savingNotes}
              icon={<Save className="w-4 h-4" />}
              size="sm"
            >
              {savingNotes ? 'Mentés...' : 'Mentés'}
            </Button>
          </>
        }
      >
        <div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
            Írd le, milyen volt az edzés, hogyan érezted magad, vagy bármilyen megjegyzést.
          </p>
          <textarea
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100 resize-none"
            rows={4}
            maxLength={2000}
            placeholder="Pl.: Nagyon jó edzés volt, sikerült új egyéni csúcsot elérnem mellnyomásban..."
            value={notesText}
            onChange={(e) => setNotesText(e.target.value)}
            autoFocus
          />
          <p className="text-xs text-gray-400 mt-1 text-right">
            {notesText.length}/2000
          </p>
        </div>
      </Modal>

      {/* Duration Editing Modal */}
      <Modal
        isOpen={!!editingDurationWorkoutId}
        onClose={() => setEditingDurationWorkoutId(null)}
        title="Edzés időtartam módosítása"
        size="md"
        footer={
          <>
            <button
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              onClick={() => setEditingDurationWorkoutId(null)}
              disabled={savingDuration}
            >
              Mégse
            </button>
            <Button
              onClick={handleSaveDuration}
              disabled={savingDuration}
              icon={<Save className="w-4 h-4" />}
              size="sm"
            >
              {savingDuration ? 'Mentés...' : 'Mentés'}
            </Button>
          </>
        }
      >
        <div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Módosítsd az edzés kezdési és befejezési idejét, ha elfelejtetted leállítani az órát.
          </p>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Kezdés ideje
              </label>
              <input
                type="datetime-local"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                value={durationStartTime}
                onChange={(e) => setDurationStartTime(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Befejezés ideje
              </label>
              <input
                type="datetime-local"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                value={durationEndTime}
                onChange={(e) => setDurationEndTime(e.target.value)}
              />
            </div>
            {durationStartTime && durationEndTime && (
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg px-3 py-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Számított időtartam:{' '}
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {formatDuration(new Date(durationStartTime).toISOString(), new Date(durationEndTime).toISOString())}
                  </span>
                </span>
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* Active Workout Warning Modal */}
      <Modal
        isOpen={showActiveWorkoutWarning}
        onClose={() => setShowActiveWorkoutWarning(false)}
        title="Aktív edzés folyamatban"
        footer={
          <>
            <button
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              onClick={() => setShowActiveWorkoutWarning(false)}
            >
              Mégse
            </button>
            <Link to="/workout">
              <Button>
                Aktív edzés megnyitása
              </Button>
            </Link>
          </>
        }
      >
        <div className="text-center py-4">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-amber-600" />
          </div>
          <p className="text-gray-700 dark:text-gray-300 text-lg mb-2">
            Már van egy folyamatban lévő edzésed!
          </p>
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            Korábbi edzés rögzítéséhez először fejezd be az aktív edzésedet.
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default Workouts;
